import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  Star, ThumbsUp, ThumbsDown, Send, CheckCircle2,
  MessageSquare, Award
} from 'lucide-react';
import api from '../services/api';

const NIVEAUX = [
  { value: 'tres_insatisfait', label: 'Très insatisfait', color: 'bg-red-500', emoji: '😞' },
  { value: 'insatisfait', label: 'Insatisfait', color: 'bg-orange-500', emoji: '😕' },
  { value: 'moyennement_satisfait', label: 'Moyennement', color: 'bg-amber-500', emoji: '😐' },
  { value: 'satisfait', label: 'Satisfait', color: 'bg-emerald-500', emoji: '🙂' },
  { value: 'tres_satisfait', label: 'Très satisfait', color: 'bg-green-600', emoji: '😊' }
];

const CritereRow = ({ critere, value, onChange, readOnly = false }) => {
  const getNiveauIndex = (val) => NIVEAUX.findIndex(n => n.value === val);
  const currentIndex = getNiveauIndex(value);

  return (
    <div className="py-4 border-b border-slate-100 last:border-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <span className="font-medium text-slate-800">{critere}</span>
        
        {readOnly ? (
          <Badge className={`${NIVEAUX[currentIndex]?.color || 'bg-slate-200'} text-white`}>
            {NIVEAUX[currentIndex]?.emoji} {NIVEAUX[currentIndex]?.label || 'Non évalué'}
          </Badge>
        ) : (
          <div className="flex gap-1">
            {NIVEAUX.map((niveau, index) => (
              <button
                key={niveau.value}
                onClick={() => onChange(critere, niveau.value)}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all
                  ${currentIndex === index 
                    ? niveau.color + ' text-white ring-2 ring-offset-2 ring-blue-500 scale-110' 
                    : 'bg-slate-100 hover:bg-slate-200'
                  }`}
                title={niveau.label}
              >
                {niveau.emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EvaluationSatisfaction = ({ sortieId = null }) => {
  const [criteres, setCriteres] = useState([]);
  const [existingEval, setExistingEval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    evaluations: {},
    commentaire_global: '',
    recommanderait: null,
    suggestions: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [criteresRes, evalRes] = await Promise.all([
        api.get('/evaluation-satisfaction/criteres'),
        api.get('/evaluation-satisfaction/me')
      ]);
      
      setCriteres(criteresRes.data.criteres || []);
      
      if (evalRes.data) {
        setExistingEval(evalRes.data);
        setFormData({
          evaluations: evalRes.data.evaluations || {},
          commentaire_global: evalRes.data.commentaire_global || '',
          recommanderait: evalRes.data.recommanderait,
          suggestions: evalRes.data.suggestions || ''
        });
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCritereChange = (critere, value) => {
    setFormData(prev => ({
      ...prev,
      evaluations: { ...prev.evaluations, [critere]: value }
    }));
  };

  const handleSubmit = async () => {
    // Vérifier que tous les critères sont évalués
    const missingCriteres = criteres.filter(c => !formData.evaluations[c]);
    if (missingCriteres.length > 0) {
      toast.error(`Veuillez évaluer tous les critères (${missingCriteres.length} manquant(s))`);
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/evaluation-satisfaction', {
        ...formData,
        sortie_id: sortieId
      });
      
      toast.success('Merci pour votre évaluation !');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const getAverageScore = () => {
    const scores = Object.values(formData.evaluations).map(v => 
      NIVEAUX.findIndex(n => n.value === v) + 1
    ).filter(s => s > 0);
    
    if (scores.length === 0) return 0;
    return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
  };

  const getProgressPercent = () => {
    return Math.round((Object.keys(formData.evaluations).length / criteres.length) * 100);
  };

  if (loading) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement...</p>
        </CardContent>
      </Card>
    );
  }

  // Si déjà évalué
  if (existingEval) {
    const avgScore = getAverageScore();
    
    return (
      <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl shadow-sm border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="w-6 h-6" />
            Évaluation de Satisfaction Enregistrée
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-center gap-8 py-4">
            <div className="text-center">
              <div className="text-5xl font-bold text-emerald-600">{avgScore}</div>
              <div className="text-sm text-slate-600">/ 5</div>
              <div className="flex gap-1 mt-2 justify-center">
                {[1,2,3,4,5].map(i => (
                  <Star 
                    key={i}
                    className={`w-5 h-5 ${i <= Math.round(avgScore) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
                  />
                ))}
              </div>
            </div>
            
            <div className="text-center px-6 border-l border-emerald-200">
              {existingEval.recommanderait === true && (
                <div className="flex flex-col items-center">
                  <ThumbsUp className="w-8 h-8 text-emerald-600" />
                  <span className="text-sm text-emerald-700 font-medium mt-1">Recommande</span>
                </div>
              )}
              {existingEval.recommanderait === false && (
                <div className="flex flex-col items-center">
                  <ThumbsDown className="w-8 h-8 text-red-500" />
                  <span className="text-sm text-red-600 font-medium mt-1">Ne recommande pas</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4">
            <h4 className="font-semibold text-slate-800 mb-3">Détail par critère</h4>
            {criteres.map(critere => (
              <CritereRow
                key={critere}
                critere={critere}
                value={formData.evaluations[critere]}
                onChange={() => {}}
                readOnly={true}
              />
            ))}
          </div>
          
          {existingEval.commentaire_global && (
            <div className="bg-white rounded-xl p-4">
              <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Votre commentaire
              </h4>
              <p className="text-slate-600">{existingEval.commentaire_global}</p>
            </div>
          )}
          
          <div className="text-center text-sm text-emerald-600">
            Merci pour votre retour ! Votre avis nous aide à améliorer nos services.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-3xl shadow-sm border-0">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
                <Award className="w-7 h-7 inline mr-2 text-purple-600" />
                Évaluation de Satisfaction
              </h2>
              <p className="text-slate-600 mt-1">
                Aidez-nous à améliorer nos services en partageant votre expérience
              </p>
            </div>
            
            <div className="text-right">
              <div className="text-3xl font-bold text-purple-600">{getProgressPercent()}%</div>
              <span className="text-sm text-slate-500">complété</span>
            </div>
          </div>
          
          {/* Barre de progression */}
          <div className="mt-4">
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${getProgressPercent()}%` }}
              ></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Critères d'évaluation */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Évaluez chaque aspect
          </CardTitle>
        </CardHeader>
        <CardContent className="px-6">
          {criteres.map(critere => (
            <CritereRow
              key={critere}
              critere={critere}
              value={formData.evaluations[critere]}
              onChange={handleCritereChange}
            />
          ))}
        </CardContent>
      </Card>

      {/* Recommandation */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardHeader>
          <CardTitle>Recommanderiez-vous ce dispositif ?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 justify-center">
            <Button
              variant={formData.recommanderait === true ? "default" : "outline"}
              onClick={() => setFormData({ ...formData, recommanderait: true })}
              className={`rounded-xl px-8 py-6 ${
                formData.recommanderait === true 
                  ? 'bg-emerald-600 hover:bg-emerald-700' 
                  : 'hover:bg-emerald-50 hover:border-emerald-300'
              }`}
            >
              <ThumbsUp className={`w-6 h-6 mr-2 ${formData.recommanderait === true ? 'text-white' : 'text-emerald-600'}`} />
              Oui
            </Button>
            <Button
              variant={formData.recommanderait === false ? "default" : "outline"}
              onClick={() => setFormData({ ...formData, recommanderait: false })}
              className={`rounded-xl px-8 py-6 ${
                formData.recommanderait === false 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'hover:bg-red-50 hover:border-red-300'
              }`}
            >
              <ThumbsDown className={`w-6 h-6 mr-2 ${formData.recommanderait === false ? 'text-white' : 'text-red-600'}`} />
              Non
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Commentaires */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            Commentaires (optionnel)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Commentaire global sur votre expérience</Label>
            <Textarea
              value={formData.commentaire_global}
              onChange={(e) => setFormData({ ...formData, commentaire_global: e.target.value })}
              placeholder="Partagez votre ressenti général..."
              className="rounded-xl min-h-[100px]"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Suggestions d'amélioration</Label>
            <Textarea
              value={formData.suggestions}
              onChange={(e) => setFormData({ ...formData, suggestions: e.target.value })}
              placeholder="Comment pourrions-nous nous améliorer ?"
              className="rounded-xl min-h-[80px]"
            />
          </div>
        </CardContent>
      </Card>

      {/* Bouton soumettre */}
      <div className="flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={submitting || Object.keys(formData.evaluations).length < criteres.length}
          className="bg-purple-600 hover:bg-purple-700 rounded-xl px-8"
        >
          <Send className="w-4 h-4 mr-2" />
          {submitting ? 'Envoi en cours...' : 'Envoyer mon évaluation'}
        </Button>
      </div>
    </div>
  );
};

export default EvaluationSatisfaction;
